import React from 'react';
import { ArrowRight, Check } from 'lucide-react';
import PricingBox from './PricingBox';
import { useAuth, usePricingPlans } from '../../../hooks';
import { usePaddleCheckout } from '../../../hooks/usePaddleCheckout';
import { Button } from '../../shared';
import { SUBSCRIPTION_PLANS, themeColorHex } from '../../../constants';
import { cn } from '../../ui/utils';

function isCurrentPlan(price, userSubscriptionPlan) {
  if (userSubscriptionPlan === SUBSCRIPTION_PLANS.PRO) {
    return price.price > 0;
  }
  return !(price.price > 0);
}

const UpgradePlans = ({ closePlans }) => {
  const { prices, isLoading } = usePricingPlans();
  const { openCheckout } = usePaddleCheckout();
  const { userSubscriptionPlan } = useAuth();
  const [selectedId, setSelectedId] = React.useState(null);

  React.useEffect(() => {
    if (!prices.length) return;
    const popular = prices.find((price) => price.popular);
    setSelectedId((popular ?? prices[0]).id);
  }, [prices]);

  if (isLoading) {
    return (
      <div className="flex min-h-[180px] w-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!prices.length) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Plans are unavailable right now. Please try again later.
      </p>
    );
  }

  const selectedPlan = prices.find((price) => price.id === selectedId) ?? prices[0];

  return (
    <div className="w-full" id="upgradeModal">
      {/* Mobile: compact chips + shared feature list */}
      <div className="md:hidden">
        <div
          className={cn(
            'grid gap-2.5',
            prices.length === 1 ? 'grid-cols-1' : 'grid-cols-2'
          )}
        >
          {prices.map((price) => {
            const isSelected = selectedPlan?.id === price.id;
            const isCurrent = isCurrentPlan(price, userSubscriptionPlan);
            return (
              <button
                key={price.id ?? price.name}
                type="button"
                onClick={() => setSelectedId(price.id)}
                className={cn(
                  'relative flex flex-col items-center rounded-xl border px-2.5 pb-3 pt-5 text-center transition-colors',
                  isSelected
                    ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                    : 'border-border bg-white',
                  isCurrent && !isSelected && 'border-emerald-300 bg-emerald-50/70',
                  isCurrent && isSelected && 'border-emerald-500 bg-emerald-50 ring-emerald-400/40'
                )}
                aria-pressed={isSelected}
                aria-current={isCurrent ? 'true' : undefined}
              >
                {isCurrent ? (
                  <span className="absolute left-0 top-0 rounded-br-lg rounded-tl-xl bg-emerald-500 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white">
                    Current
                  </span>
                ) : null}
                {price.popular ? (
                  <span
                    className="absolute right-0 top-0 rounded-bl-lg rounded-tr-xl px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white"
                    style={{ backgroundColor: themeColorHex }}
                  >
                    Popular
                  </span>
                ) : null}
                <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {price.name}
                </span>
                <span className="mt-1.5 text-lg font-bold text-foreground">
                  {price.price > 0 ? `${price.currency}${price.price}` : 'Free'}
                </span>
                {price.price > 0 && price.interval ? (
                  <span className="text-[11px] text-muted-foreground">/ {price.interval}</span>
                ) : (
                  <span className="text-[11px] text-transparent">-</span>
                )}
              </button>
            );
          })}
        </div>

        {selectedPlan?.features?.length ? (
          <ul className="mt-4 space-y-2.5">
            {selectedPlan.features.map((feature, index) => (
              <li
                key={`${selectedPlan.id}-m-feature-${index}`}
                className="flex items-start gap-2 text-[13px] leading-snug text-foreground/90"
              >
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-green-500" aria-hidden />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        ) : null}

        <div className="sticky bottom-0 mt-4 bg-gradient-to-t from-white via-white to-white/80 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2">
          {selectedPlan?.price > 0 ? (
            <Button
              type="button"
              buttonType="primary"
              className="h-11 w-full gap-2"
              onClick={() => {
                openCheckout({ priceId: selectedPlan.id });
                closePlans();
              }}
            >
              Buy {selectedPlan.name}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Button>
          ) : (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-center text-sm font-medium text-emerald-800">
              Your current plan
            </div>
          )}
          {selectedPlan?.info ? (
            <p className="mt-2 text-center text-[11px] leading-relaxed text-muted-foreground">
              {selectedPlan.info}
            </p>
          ) : null}
        </div>
      </div>

      {/* Desktop: all plans in one row, each with its own description/features */}
      <div
        className={cn(
          'hidden md:grid md:gap-4',
          prices.length === 1 && 'md:mx-auto md:max-w-sm md:grid-cols-1',
          prices.length === 2 && 'md:grid-cols-2',
          prices.length >= 3 && 'md:grid-cols-3'
        )}
      >
        {prices.map((price) => (
          <PricingBox
            key={price.id ?? price.name}
            closePlans={closePlans}
            isCurrent={isCurrentPlan(price, userSubscriptionPlan)}
            {...price}
          />
        ))}
      </div>
    </div>
  );
};

export default UpgradePlans;
